import React, { useEffect, useMemo, useState } from "react";
import { FaExclamationTriangle } from "react-icons/fa";
import "./ProgramLifecycleCancelWizard.css";

/**
 * ProgramLifecycleCancelWizard — 4-step wizard for hard-cancelling a corp
 * partnership program (Story 2 CORP-PARTNERSHIP-HARD-CANCEL-CORE-001).
 * Amended per Roderick's 2026-06-30 locks (Q1 auto-cancel-only, Q4 no
 * refund workflow, Q7 no auto-comms): the original 6-step wizard collapsed
 * to 4 steps. Refund summary + comms + convert-to-paid all DROPPED — admin
 * handles those manually outside the platform.
 *
 * Steps (Zuri visual spec §3 amended):
 *   1. Reason (required, >= 10 chars per ADR-046 D8 convention + ADR-049 D6)
 *   2. Deactivation timing — Immediately OR Grace period (date picker)
 *   3. Bookings-treatment acknowledgment (single option; must tick to
 *      continue). Convert-to-paid dropped; auto-cancel is the only outcome.
 *   4. Final confirm — summary of prior 3 steps + red "Cancel Partnership"
 *      button + manual-follow-up reminder card
 *
 * On confirm the wizard emits `{ reason, deactivateAt, bookingsTreatment }`.
 * `deactivateAt` is null for Immediately and an ISO date string for Grace.
 * `bookingsTreatment` is always `"auto-cancel"` (single option per Q1 lock).
 *
 * The wizard is CFG=YES: this is a customer-facing money-adjacent action
 * that flips access + auto-cancels bookings. Backend enforces the actual
 * state transitions + guards (ADR-049 D3); the wizard's role is deliberate
 * step-through so an operator doesn't fire the destructive action by
 * accident.
 *
 * Props:
 *  - isOpen: boolean
 *  - programName: string
 *  - onClose: () => void
 *  - onConfirm: ({ reason, deactivateAt, bookingsTreatment }) => void
 *  - inFlight: boolean
 *  - error: string | null
 */
const TOTAL_STEPS = 4;

const emptyForm = () => ({
  reason: "",
  timing: null, // null | 'immediate' | 'grace'
  graceDate: "", // yyyy-mm-dd from <input type="date">
  acknowledged: false,
});

const isoAtStartOfDay = (yyyymmdd) => {
  // Convert the yyyy-mm-dd from <input type="date"> to an ISO string.
  // We keep this timezone-naive on the wire and let the BE + cron interpret
  // in America/Phoenix (per D3 timing semantics: "12:00 AM MST on selected
  // date"). Emitting `${yyyymmdd}T00:00:00.000Z` here would be misleading;
  // instead we emit the date at local midnight in the browser and let the
  // BE do the timezone adjustment — same convention as the existing
  // bookings/availability endpoints that accept a `YYYY-MM-DD` date.
  if (!yyyymmdd) return null;
  return `${yyyymmdd}T00:00:00.000`;
};

const formatDisplayDate = (yyyymmdd) => {
  if (!yyyymmdd) return "";
  const [y, m, d] = yyyymmdd.split("-");
  if (!y || !m || !d) return yyyymmdd;
  return `${m}/${d}/${y}`;
};

const minGraceDate = () => {
  const tomorrow = new Date();
  tomorrow.setDate(tomorrow.getDate() + 1);
  return tomorrow.toISOString().split("T")[0];
};

const maxGraceDate = () => {
  const plus365 = new Date();
  plus365.setDate(plus365.getDate() + 365);
  return plus365.toISOString().split("T")[0];
};

const ProgramLifecycleCancelWizard = ({
  isOpen,
  programName,
  onClose,
  onConfirm,
  inFlight = false,
  error = null,
}) => {
  const [step, setStep] = useState(1);
  const [form, setForm] = useState(emptyForm());

  // Reset wizard whenever it opens so a re-open after error/close never
  // leaks state (matches ProgramLifecycleActionModal convention).
  useEffect(() => {
    if (isOpen) {
      setStep(1);
      setForm(emptyForm());
    }
  }, [isOpen]);

  // ESC closes when not in-flight. Per Zuri §3.1 + accessibility spec §8:
  // ESC is the standard modal-dismiss key. We don't open a "discard changes"
  // sub-modal on ESC — the simpler UX is: ESC = close = discard, same as
  // Cancel button. If the operator has typed a long reason they don't want
  // to lose, the Cancel button is next to Continue on every step.
  useEffect(() => {
    if (!isOpen) return undefined;
    const handler = (event) => {
      if (event.key === "Escape" && !inFlight) {
        onClose?.();
      }
    };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [isOpen, inFlight, onClose]);

  const trimmedReason = form.reason.trim();
  const reasonValid = trimmedReason.length >= 10;

  const timingValid = useMemo(() => {
    if (form.timing === "immediate") return true;
    if (form.timing === "grace" && form.graceDate) {
      // Additional guard: the picked date must not be in the past. The
      // <input type="date" min=...> already enforces this in the browser
      // widget, but a paste-in past date would slip through — we re-check.
      const min = minGraceDate();
      return form.graceDate >= min;
    }
    return false;
  }, [form.timing, form.graceDate]);

  const canProceedFromStep = (n) => {
    if (n === 1) return reasonValid;
    if (n === 2) return timingValid;
    if (n === 3) return form.acknowledged;
    if (n === 4) return !inFlight;
    return false;
  };

  if (!isOpen) return null;

  const displayName = programName || "this program";

  const handleBackdropClick = () => {
    if (inFlight) return;
    onClose?.();
  };

  const handleContinue = () => {
    if (!canProceedFromStep(step)) return;
    setStep((prev) => Math.min(prev + 1, TOTAL_STEPS));
  };

  const handleBack = () => {
    if (inFlight) return;
    setStep((prev) => Math.max(prev - 1, 1));
  };

  const handleConfirm = () => {
    if (!canProceedFromStep(4)) return;
    const payload = {
      reason: trimmedReason,
      deactivateAt:
        form.timing === "immediate" ? null : isoAtStartOfDay(form.graceDate),
      bookingsTreatment: "auto-cancel",
    };
    onConfirm?.(payload);
  };

  const progressPct = (step / TOTAL_STEPS) * 100;

  return (
    <div
      className="program-lifecycle-cancel-wizard-backdrop"
      onMouseDown={handleBackdropClick}
      role="presentation"
    >
      <div
        className="program-lifecycle-cancel-wizard"
        role="dialog"
        aria-modal="true"
        aria-labelledby="program-lifecycle-cancel-wizard-title"
        onMouseDown={(event) => event.stopPropagation()}
      >
        <div className="program-lifecycle-cancel-wizard-header">
          <h2
            id="program-lifecycle-cancel-wizard-title"
            className="program-lifecycle-cancel-wizard-title"
          >
            Cancel {displayName}
          </h2>
          <span
            className="program-lifecycle-cancel-wizard-step-indicator"
            data-testid="wizard-step-indicator"
          >
            Step {step} of {TOTAL_STEPS}
          </span>
        </div>

        <div
          className="program-lifecycle-cancel-wizard-progress"
          role="progressbar"
          aria-valuenow={step}
          aria-valuemin={1}
          aria-valuemax={TOTAL_STEPS}
        >
          <div
            className="program-lifecycle-cancel-wizard-progress-fill"
            style={{ width: `${progressPct}%` }}
          />
        </div>

        {error ? (
          <div
            className="program-lifecycle-cancel-wizard-error"
            role="alert"
          >
            <span
              className="program-lifecycle-cancel-wizard-error-icon"
              aria-hidden="true"
            >
              <FaExclamationTriangle />
            </span>
            <div className="program-lifecycle-cancel-wizard-error-body">
              <strong className="program-lifecycle-cancel-wizard-error-title">
                Cancel failed
              </strong>
              <p className="program-lifecycle-cancel-wizard-error-text">
                {error}
              </p>
            </div>
          </div>
        ) : null}

        <div
          className="program-lifecycle-cancel-wizard-body"
          data-testid={`wizard-step-${step}`}
        >
          {step === 1 ? (
            <>
              <h3 className="program-lifecycle-cancel-wizard-step-title">
                Why are we cancelling this partnership?
              </h3>
              <p className="program-lifecycle-cancel-wizard-step-body">
                This will be logged for audit. Refunds and customer
                communications are handled manually in Stripe and by admin.
              </p>
              <label
                htmlFor="cancel-wizard-reason"
                className="program-lifecycle-cancel-wizard-label"
              >
                Reason (required)
              </label>
              <textarea
                id="cancel-wizard-reason"
                className={`program-lifecycle-cancel-wizard-textarea${
                  !reasonValid && trimmedReason.length > 0 ? " is-invalid" : ""
                }`}
                rows={5}
                value={form.reason}
                onChange={(event) =>
                  setForm((prev) => ({ ...prev, reason: event.target.value }))
                }
                placeholder="e.g. Contract not renewed for 2027; org restructure; partnership dispute..."
                disabled={inFlight}
                aria-invalid={!reasonValid && trimmedReason.length > 0}
                aria-describedby="cancel-wizard-reason-counter"
              />
              <p
                id="cancel-wizard-reason-counter"
                className={`program-lifecycle-cancel-wizard-helper${
                  !reasonValid && trimmedReason.length > 0
                    ? " is-invalid"
                    : ""
                }`}
              >
                {trimmedReason.length} / minimum 10
              </p>
            </>
          ) : null}

          {step === 2 ? (
            <>
              <h3 className="program-lifecycle-cancel-wizard-step-title">
                When should enrolled members lose access?
              </h3>
              <p className="program-lifecycle-cancel-wizard-step-body">
                Choose whether access ends immediately or after a grace
                period.
              </p>

              <div className="program-lifecycle-cancel-wizard-radio-group">
                <label className="program-lifecycle-cancel-wizard-radio-option">
                  <input
                    type="radio"
                    name="cancel-timing"
                    value="immediate"
                    checked={form.timing === "immediate"}
                    onChange={() =>
                      setForm((prev) => ({
                        ...prev,
                        timing: "immediate",
                        graceDate: "",
                      }))
                    }
                    disabled={inFlight}
                  />
                  <div className="program-lifecycle-cancel-wizard-radio-content">
                    <strong className="program-lifecycle-cancel-wizard-radio-label">
                      Immediately
                    </strong>
                    <span className="program-lifecycle-cancel-wizard-radio-sublabel">
                      Enrollments deactivate today. Members lose corp-rate
                      access on save.
                    </span>
                  </div>
                </label>

                <label className="program-lifecycle-cancel-wizard-radio-option">
                  <input
                    type="radio"
                    name="cancel-timing"
                    value="grace"
                    checked={form.timing === "grace"}
                    onChange={() =>
                      setForm((prev) => ({ ...prev, timing: "grace" }))
                    }
                    disabled={inFlight}
                  />
                  <div className="program-lifecycle-cancel-wizard-radio-content">
                    <strong className="program-lifecycle-cancel-wizard-radio-label">
                      Grace period
                    </strong>
                    <span className="program-lifecycle-cancel-wizard-radio-sublabel">
                      Members keep access until a date you choose.
                    </span>
                    {form.timing === "grace" ? (
                      <div className="program-lifecycle-cancel-wizard-date-picker">
                        <label
                          htmlFor="cancel-wizard-grace-date"
                          className="program-lifecycle-cancel-wizard-date-label"
                        >
                          End date
                        </label>
                        <input
                          id="cancel-wizard-grace-date"
                          type="date"
                          className="program-lifecycle-cancel-wizard-date-input"
                          value={form.graceDate}
                          min={minGraceDate()}
                          max={maxGraceDate()}
                          onChange={(event) =>
                            setForm((prev) => ({
                              ...prev,
                              graceDate: event.target.value,
                            }))
                          }
                          disabled={inFlight}
                        />
                        <span className="program-lifecycle-cancel-wizard-date-note">
                          Deactivation runs 12:00 AM MST on selected date.
                        </span>
                      </div>
                    ) : null}
                  </div>
                </label>
              </div>
            </>
          ) : null}

          {step === 3 ? (
            <>
              <h3 className="program-lifecycle-cancel-wizard-step-title">
                Already-booked corp sessions will be auto-cancelled
              </h3>
              <p className="program-lifecycle-cancel-wizard-step-body">
                All future corp-rate and sponsored bookings for enrolled
                members will be cancelled automatically.
              </p>

              <div className="program-lifecycle-cancel-wizard-notice">
                <p className="program-lifecycle-cancel-wizard-notice-headline">
                  All future corp-rate and sponsored bookings will be
                  auto-cancelled.
                </p>
                <p className="program-lifecycle-cancel-wizard-notice-body">
                  Refunds are handled per each booking's payment state
                  (existing booking-refund policy). Admin manages Stripe
                  refunds manually outside this flow.
                </p>
                <p className="program-lifecycle-cancel-wizard-notice-body">
                  Customer notification is handled manually by the corp
                  partner's HR admin (auto-comms not in scope).
                </p>
              </div>

              <label className="program-lifecycle-cancel-wizard-acknowledgment">
                <input
                  type="checkbox"
                  checked={form.acknowledged}
                  onChange={(event) =>
                    setForm((prev) => ({
                      ...prev,
                      acknowledged: event.target.checked,
                    }))
                  }
                  disabled={inFlight}
                />
                <span>
                  I understand all corp-rate and sponsored bookings will be
                  auto-cancelled.
                </span>
              </label>
            </>
          ) : null}

          {step === 4 ? (
            <>
              <h3 className="program-lifecycle-cancel-wizard-step-title">
                Ready to cancel {displayName}?
              </h3>
              <p className="program-lifecycle-cancel-wizard-step-body">
                Review the summary. This action is reversible via Restore
                (available in a future release). Refunds and customer
                communications are handled manually after this action.
              </p>

              <div
                className="program-lifecycle-cancel-wizard-summary"
                data-testid="cancel-wizard-summary"
              >
                <div className="program-lifecycle-cancel-wizard-summary-row">
                  <span className="program-lifecycle-cancel-wizard-summary-label">
                    Reason
                  </span>
                  <span className="program-lifecycle-cancel-wizard-summary-value">
                    {trimmedReason}
                  </span>
                </div>
                <div className="program-lifecycle-cancel-wizard-summary-row">
                  <span className="program-lifecycle-cancel-wizard-summary-label">
                    Deactivation
                  </span>
                  <span className="program-lifecycle-cancel-wizard-summary-value">
                    {form.timing === "immediate"
                      ? "Immediately"
                      : `Grace period until ${formatDisplayDate(form.graceDate)}`}
                  </span>
                </div>
                <div className="program-lifecycle-cancel-wizard-summary-row">
                  <span className="program-lifecycle-cancel-wizard-summary-label">
                    Future bookings
                  </span>
                  <span className="program-lifecycle-cancel-wizard-summary-value">
                    Auto-cancel all corp-rate and sponsored bookings
                  </span>
                </div>
              </div>

              <div className="program-lifecycle-cancel-wizard-follow-up">
                <strong className="program-lifecycle-cancel-wizard-follow-up-title">
                  Manual follow-up required
                </strong>
                <ul className="program-lifecycle-cancel-wizard-follow-up-list">
                  <li>
                    Issue refunds for unused corp-rate credits in Stripe.
                  </li>
                  <li>
                    Notify corp partner HR admin so they can inform affected
                    employees.
                  </li>
                </ul>
              </div>
            </>
          ) : null}
        </div>

        <div className="program-lifecycle-cancel-wizard-actions">
          <button
            type="button"
            className="program-lifecycle-cancel-wizard-cancel"
            onClick={onClose}
            disabled={inFlight}
          >
            Cancel
          </button>
          {step > 1 ? (
            <button
              type="button"
              className="program-lifecycle-cancel-wizard-back"
              onClick={handleBack}
              disabled={inFlight}
            >
              Back
            </button>
          ) : null}
          {step < TOTAL_STEPS ? (
            <button
              type="button"
              className="program-lifecycle-cancel-wizard-continue"
              onClick={handleContinue}
              disabled={!canProceedFromStep(step) || inFlight}
            >
              Continue
            </button>
          ) : (
            <button
              type="button"
              className="program-lifecycle-cancel-wizard-confirm"
              onClick={handleConfirm}
              disabled={!canProceedFromStep(4)}
            >
              {inFlight ? "Cancelling..." : "Cancel Partnership"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProgramLifecycleCancelWizard;
